import Config from 'Config';
import { SubmissionError, stopSubmit, reset } from 'redux-form';
import { CALL_API, processErrorResponse, getDefaultRequestHeaders } from '../common/middleware/api';
import browserStorage from '../common/services/browserStorage';
import globalize from '../common/services/globalize';

export const LOGIN_REQUEST = 'LOGIN_REQUEST';
export const LOGIN_SUCCESS = 'LOGIN_SUCCESS';
export const LOGIN_FAILURE = 'LOGIN_FAILURE';

export const REFRESH_TOKEN_REQUEST = 'REFRESH_TOKEN_REQUEST';
export const REFRESH_TOKEN_SUCCESS = 'REFRESH_TOKEN_SUCCESS';
export const REFRESH_TOKEN_FAILURE = 'REFRESH_TOKEN_FAILURE';

export const LOGOUT = 'LOGOUT';

export const CURRENT_USER_REQUEST = 'CURRENT_USER_REQUEST';
export const CURRENT_USER_SUCCESS = 'CURRENT_USER_SUCCESS';
export const CURRENT_USER_FAILURE = 'CURRENT_USER_FAILURE';

export const REGISTER_PARENT_REQUEST = 'REGISTER_PARENT_REQUEST';
export const REGISTER_PARENT_SUCCESS = 'REGISTER_PARENT_SUCCESS';
export const REGISTER_PARENT_FAILURE = 'REGISTER_PARENT_FAILURE';

export const FORGOTTEN_PASSWORD_REQUEST = 'FORGOTTEN_PASSWORD_REQUEST';
export const FORGOTTEN_PASSWORD_SUCCESS = 'FORGOTTEN_PASSWORD_SUCCESS';
export const FORGOTTEN_PASSWORD_FAILURE = 'FORGOTTEN_PASSWORD_FAILURE';

export const RESET_PASSWORD_REQUEST = 'RESET_PASSWORD_REQUEST';
export const RESET_PASSWORD_SUCCESS = 'RESET_PASSWORD_SUCCESS';
export const RESET_PASSWORD_FAILURE = 'RESET_PASSWORD_FAILURE';

export const UNSUBSCRIBE_REQUEST = 'UNSUBSCRIBE_REQUEST';
export const UNSUBSCRIBE_SUCCESS = 'UNSUBSCRIBE_SUCCESS';
export const UNSUBSCRIBE_FAILURE = 'UNSUBSCRIBE_FAILURE';

const TOKEN_URL = `${Config.authServerUrl}/token`;
const CURRENT_USER_URL = `${Config.frontofficeApiUrl}/api/account`;
const PARENT_REGISTRATION_URL = `${Config.frontofficeApiUrl}/api/parents`;
const PARENT_UNSUBSCRIBE_URL = `${Config.frontofficeApiUrl}/api/unsubscribe`;

const requestLogin = (credentials) => ({
	type: LOGIN_REQUEST,
	payload: credentials
});

const receiveLogin = (json) => ({
	type: LOGIN_SUCCESS,
	payload: {
		accessToken: json.access_token,
		refreshToken: json.refresh_token
	},
	analytics: {
		event: 'LogIn',
		isCustomEvent: true
	}
});

const loginError = (errorMessage) => ({
	type: LOGIN_FAILURE,
	payload: { error: errorMessage }
});

export const loginUser = (credentials) => (dispatch, getState) => {
	const { username, password, impersonatedUsername } = credentials;
	const state = getState();
	const defaultHeaders = getDefaultRequestHeaders(state);
	const config = {
		method: 'POST',
		headers: {
			...defaultHeaders,
			'Content-Type': 'application/x-www-form-urlencoded'
		},
		body: `grant_type=password&username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}&client_id=front_office&scope=front_office_api%20offline_access`
	};
	if (impersonatedUsername) {
		config.body = `${config.body}&impersonated_username=${encodeURIComponent(impersonatedUsername)}`;
	}
	dispatch(requestLogin(credentials));
	return fetch(TOKEN_URL, config)
		.then(response => response.json().then(json => {
			if (!response.ok) {
				const errorMessage = json.error_description ? json.error_description : json.error;
				dispatch(loginError(errorMessage));
				throw new SubmissionError({			// handled by redux-form
					_error: errorMessage
				});
			} else {
				browserStorage.setToLocalStorage(state.configuration.franchisor, 'accessToken', json.access_token);
				browserStorage.setToLocalStorage(state.configuration.franchisor, 'refreshToken', json.refresh_token);

				dispatch(receiveLogin(json));
				dispatch(getCurrentUser(json.access_token));
			}
		}));
};

const requestRefreshToken = (initializing) => ({
	type: REFRESH_TOKEN_REQUEST,
	payload: { initializing }
});

const receiveRefreshToken = (json) => ({
	type: REFRESH_TOKEN_SUCCESS,
	payload: {
		accessToken: json.access_token,
		refreshToken: json.refresh_token
	}
});

const refreshTokenError = (errorMessage) => ({
	type: REFRESH_TOKEN_FAILURE,
	payload: { error: errorMessage }
});

let isRefreshingToken = false;
let refreshTokenPromise = null;

export const refreshToken = (initializing) => (dispatch, getState) => {
	if (!isRefreshingToken) {
		const state = getState();
		const refreshToken = browserStorage.getFromLocalStorage(state.configuration.franchisor, 'refreshToken');

		if (!refreshToken) {
			const errorMessage = 'Refresh token is missing';
			dispatch(logoutUser(false));
			dispatch(refreshTokenError(errorMessage));
			return Promise.resolve(false);
		}

		isRefreshingToken = true;

		const defaultHeaders = getDefaultRequestHeaders(state);

		const config = {
			method: 'POST',
			headers: {
				...defaultHeaders,
				'Content-Type': 'application/x-www-form-urlencoded'
			},
			body: `grant_type=refresh_token&refresh_token=${encodeURIComponent(refreshToken)}&client_id=front_office`
		};

		dispatch(requestRefreshToken(initializing));

		refreshTokenPromise = fetch(TOKEN_URL, config)
			.then(response => {
				isRefreshingToken = false;
				return response.json().then(json => {
					if (!response.ok) {
						dispatch(logoutUser(false));
						dispatch(refreshTokenError(json.error_description ? json.error_description : json.error));
						return Promise.resolve(false);
					} else {
						browserStorage.setToLocalStorage(state.configuration.franchisor, 'accessToken', json.access_token);
						browserStorage.setToLocalStorage(state.configuration.franchisor, 'refreshToken',json.refresh_token);

						dispatch(receiveRefreshToken(json));
						dispatch(getCurrentUser(json.access_token));
						return Promise.resolve(true);
					}
				});
			}).catch((error) => {
				isRefreshingToken = false;
				return Promise.resolve(false);
			});
	}

	return refreshTokenPromise;
};

export const logoutUser = (enableAnalytics = true) => (dispatch, getState) => {
	const { configuration } = getState();
	browserStorage.removeFromLocalStorage(configuration.franchisor, 'accessToken');
	browserStorage.removeFromLocalStorage(configuration.franchisor, 'refreshToken');

	// reset the default time zone to local
	globalize.setDefaultTimeZone();

	dispatch({
		type: LOGOUT,
		analytics: enableAnalytics ? {
			event: 'LogOut',
			isCustomEvent: true
		} : null
	});
};

const requestGetCurrentUser = () => ({
	type: CURRENT_USER_REQUEST
});

const receiveGetCurrentUser = (user) => ({
	type: CURRENT_USER_SUCCESS,
	payload: user
});

const getCurrentUserError = (errorMessage) => ({
	type: CURRENT_USER_FAILURE,
	payload: { error: errorMessage }
});

export const getCurrentUser = (accessToken) => (dispatch, getState) => {
	const config = {
		method: 'GET',
		headers: {
			...getDefaultRequestHeaders(getState()),
			'Authorization': 'Bearer ' + accessToken
		}
	};
	dispatch(requestGetCurrentUser());
	return fetch(CURRENT_USER_URL, config)
		.then(response => response.json().then(json => {
			if (!response.ok) {
				const errors = processErrorResponse(json);
				dispatch(getCurrentUserError(errors.message));
			} else {
				dispatch(receiveGetCurrentUser(json));
			}
		}));
};

const requestRegisterParentUser = (registrationData) => ({
	type: REGISTER_PARENT_REQUEST,
	payload: registrationData
});

const receiveRegisterParentUser = (json) => ({
	type: REGISTER_PARENT_SUCCESS,
	analytics: [
		{ event: 'SuccessfulRegistration', isCustomEvent: true },
		{ event: 'CompleteRegistration' }
	]
});

const registerParentUserError = (errorMessage) => ({
	type: REGISTER_PARENT_FAILURE,
	payload: { error: errorMessage }
});

export const registerParentUser = (registrationData) => (dispatch, getState) => {
	const { email, password } = registrationData;
	const config = {
		method: 'POST',
		headers: getDefaultRequestHeaders(getState()),
		body: JSON.stringify(registrationData)
	};
	dispatch(requestRegisterParentUser(registrationData));
	return fetch(PARENT_REGISTRATION_URL, config)
		.then(response => response.json().then(json => {
			if (!response.ok) {
				const errors = processErrorResponse(json);
				dispatch(registerParentUserError(errors.message));
				throw new SubmissionError({			// handled by redux-form
					_error: errors.message,
					...errors.validationErrors
				});
			} else {
				dispatch(receiveRegisterParentUser());
				dispatch(loginUser({ username: email, password }));
			}
		}));
};

export const forgotPassword = (username) => dispatch => {
	dispatch({
		[CALL_API]: {
			types: [FORGOTTEN_PASSWORD_REQUEST, FORGOTTEN_PASSWORD_SUCCESS, FORGOTTEN_PASSWORD_FAILURE],
			endpoint: `api/passwordReset`,
			method: 'POST',
			authorize: false,
			request: username,
			form: 'forgottenPassword',
			onSuccess: () => dispatch(reset('forgottenPassword'))
		}
	});
};

export const resetPassword = (credentials, history) => ({
	[CALL_API]: {
		types: [RESET_PASSWORD_REQUEST, RESET_PASSWORD_SUCCESS, RESET_PASSWORD_FAILURE],
		endpoint: `api/passwordReset`,
		method: 'PUT',
		authorize: false,
		request: credentials,
		form: 'resetPassword',
		transition: {
			history,
			success: {
				push: true,
				path: '/login'
			}
		}
	}
});

export const unsubscribe = (parentToken, configuration, onSuccess, onError) =>  {
	const config = {
		method: 'POST',
		headers: getDefaultRequestHeaders({ configuration }),
		body: JSON.stringify({ parentToken })
	};
	return fetch(PARENT_UNSUBSCRIBE_URL, config)
		.then(response => {
			if (!response.ok) {
				onError();
			} else {
				onSuccess();
			}
		});
};